import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Button, Select, formatSelectOptions } from '../../../components'
import { useMessage } from '../../../hooks'
import { networkingEventService } from '../../../services'
import { useNetworkDialog } from '../hooks/useNetworkDialog'
import { projectNetworking, NETWORKING_TYPE_LABELS } from '../helpers'
import { FreeText } from '../components'
import { appName } from '../appConfig'

const fmtDate = (ts) => (ts ? String(ts).slice(0, 10) : '')

// Filter bar — same hand-rolled pattern as kayentaStories' Participants.js/StoryList.js
// (no shared FilterBar component exists yet): dropdown options are derived from the
// loaded entries themselves rather than a hardcoded list, so a new type/author/person
// just shows up automatically. All three are multi-select (isMulti on the shared
// Select — see feedback's Analyze.js for the established value-toggle pattern this
// mirrors) so e.g. "Sälj OR Rekrytering" is a single filter, not two round trips.
const MULTI_FILTER_KEYS = ['type', 'author', 'person']
const EMPTY_FILTERS = { type: [], author: [], person: [] }

const applyNetworkingFilters = (entries, filters) => entries.filter(n => {
  if (filters.type.length && !filters.type.some(f => f.value === n.type)) return false
  if (filters.author.length && !filters.author.some(f => f.value === n.author)) return false
  if (filters.person.length && !filters.person.some(f => f.value === n.person_uuid)) return false
  return true
})

const deriveTypeOptions = (entries) => [...new Set(entries.map(n => n.type).filter(Boolean))]
  .map(type => ({ value: type, label: NETWORKING_TYPE_LABELS[type] || type }))
  .sort((a, b) => a.label.localeCompare(b.label, 'sv'))

const deriveAuthorOptions = (entries) => [...new Set(entries.map(n => n.author).filter(Boolean))]
  .sort((a, b) => a.localeCompare(b, 'sv'))
  .map(author => ({ value: author, label: author }))

// The person NÄTVERKAD WITH (not the author) — dedup by uuid since the same
// contact can appear in multiple entries.
const derivePersonOptions = (entries) => {
  const byUuid = new Map()
  entries.forEach(n => {
    if (n.person_uuid && !byUuid.has(n.person_uuid)) byUuid.set(n.person_uuid, n.person_name || 'Okänd person')
  })
  return [...byUuid.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'sv'))
}

// Cross-person feed of all networking events (Nätverkande), newest first. A pure
// aggregate over person_event — the same projectNetworking fold used per-person
// on PersonDetail, here keyed across everyone by source_ref. Add/edit/delete are
// available here too (the create dialog picks the person; edit/delete act on the
// entry's own person), all reusing the shared useNetworkDialog flows.
const NetworkingOverview = () => {
  const { showMessage } = useMessage()
  const { showNetworkingDialog, showDeleteNetworkingDialog } = useNetworkDialog()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  // Rows collapse to name/role/company/type; author/purpose/result/next_step live
  // in a per-row expandable section, toggled by source_ref.
  const [expanded, setExpanded] = useState(new Set())
  const [filters, setFilters] = useState(EMPTY_FILTERS)

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))

  // Adapter for the shared Input/Select onChange duality (see StoryList.js/Participants.js),
  // plus the multi-select toggle: the shared Select's isMulti mode always reports the
  // single option that was clicked (option.value), so the consumer adds/removes it from
  // its own array — same pattern as feedback's Analyze.js.
  const handleFilterChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event
    if (MULTI_FILTER_KEYS.includes(name)) {
      setFilters(prev => {
        const current = prev[name]
        const isSelected = current.some(f => f.value === value)
        const next = isSelected ? current.filter(f => f.value !== value) : [...current, { value }]
        return { ...prev, [name]: next }
      })
    } else {
      updateFilter(name, value || '')
    }
  }

  const load = useCallback((signal) => {
    setLoading(true)
    return networkingEventService.getAll(signal)
      .then(res => { setEntries(projectNetworking(res.networkingEvents || [])); setLoading(false) })
      .catch(err => { if (err.name !== 'AbortError') { showMessage('Kunde inte ladda nätverkanden', 'error'); setLoading(false) } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const ctrl = new AbortController()
    load(ctrl.signal)
    return () => ctrl.abort()
  }, [load])

  const personOf = (n) => ({ person_uuid: n.person_uuid, person_name: n.person_name })

  const toggleExpand = (ref) => {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(ref) ? next.delete(ref) : next.add(ref)
      return next
    })
  }

  return (
    <div className="network-page">
      <div className="network-detail-header">
        <h2>Nätverkande</h2>
        <div className="network-detail-header-actions">
          <Button value="Registrera nätverkande" className="button main-button" onClick={() => showNetworkingDialog(null, null, () => load())} />
        </div>
      </div>
      <p className="form-hint">Alla registrerade nätverkanden, senaste först.</p>

      {!loading && entries.length > 0 &&
        <div className="network-filter-bar">
          <Select
            className="network-filter-select"
            name="type"
            value={filters.type}
            showMultiLabel
            isMulti
            placeholder="Typ"
            options={formatSelectOptions({ options: deriveTypeOptions(entries), optionValue: 'value', optionLabel: 'label' })}
            onChange={handleFilterChange}
          />
          <Select
            className="network-filter-select"
            name="author"
            value={filters.author}
            showMultiLabel
            isMulti
            placeholder="Nätverkare"
            options={formatSelectOptions({ options: deriveAuthorOptions(entries), optionValue: 'value', optionLabel: 'label' })}
            onChange={handleFilterChange}
          />
          <Select
            className="network-filter-select"
            name="person"
            value={filters.person}
            showMultiLabel
            isMulti
            placeholder="Nätverkad med"
            options={formatSelectOptions({ options: derivePersonOptions(entries), optionValue: 'value', optionLabel: 'label' })}
            onChange={handleFilterChange}
          />
          {Object.values(filters).some(f => f.length > 0) &&
            <Button value="Rensa filter" className="button network-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)} />
          }
        </div>
      }

      {loading
        ? <p className="empty-text">Laddar…</p>
        : entries.length === 0
          ? <p className="empty-text">Inga nätverkanden ännu.</p>
          : applyNetworkingFilters(entries, filters).length === 0
            ? <p className="empty-text">Inga nätverkanden matchar filtren.</p>
            : <div className="network-detail-card">
              {applyNetworkingFilters(entries, filters).map(n => {
                const isOpen = expanded.has(n.source_ref)
                // "Nätverkare" (author) is always visible — only the free-text
                // purpose/result/next_step collapse into the expandable section.
                const hasDetails = Boolean(n.purpose || n.result || n.next_step)
                return (
                  <div className="network-inv-row network-networking-row" key={n.source_ref}>
                    <div className="network-networking-row-top">
                      <span className="network-inv-title">
                        🤝 <Link className="network-inv-link" to={`/${appName}/persons/${n.person_uuid}`}>{n.person_name || 'Okänd person'}</Link>
                        {n.role ? ` · ${n.role}` : ''}{n.company_name ? ` · ${n.company_name}` : ''}
                      </span>
                      <span className="pill network-networking-type">{NETWORKING_TYPE_LABELS[n.type] || n.type}</span>
                      <span className="network-event-date">{fmtDate(n.occurred_at)}</span>
                      <div className="network-inv-row-actions">
                        <button type="button" className="button-icon" title="Redigera" onClick={() => showNetworkingDialog(personOf(n), n, () => load())}>✎</button>
                        <button type="button" className="button-icon" title="Ta bort" onClick={() => showDeleteNetworkingDialog(n, () => load())}>🗑</button>
                      </div>
                    </div>
                    {n.author &&
                      <div className="network-event-text">
                        <span className="network-event-sub">Nätverkare: {n.author}</span>
                      </div>
                    }
                    {hasDetails &&
                      <button
                        type="button"
                        className="network-expand-toggle"
                        onClick={() => toggleExpand(n.source_ref)}
                      >
                        <span className={`network-expand-chevron${isOpen ? ' open' : ''}`}>▸</span>
                        {isOpen ? 'Dölj detaljer' : 'Visa detaljer'}
                      </button>
                    }
                    {isOpen && hasDetails &&
                      <div className="network-event-text network-networking-details">
                        {n.purpose && <div className="network-event-fact"><span className="network-event-fact-label">Syfte</span><FreeText text={n.purpose} className="network-event-sub network-event-freetext" /></div>}
                        {n.result && <div className="network-event-fact"><span className="network-event-fact-label">Resultat</span><FreeText text={n.result} className="network-event-sub network-event-freetext" /></div>}
                        {n.next_step && <div className="network-event-fact"><span className="network-event-fact-label">Nästa steg</span><FreeText text={n.next_step} className="network-event-sub network-event-freetext" /></div>}
                      </div>
                    }
                  </div>
                )
              })}
            </div>
      }
    </div>
  )
}

export default NetworkingOverview
